// A job's progress, worked out from the steps it goes through and its cards. One figure,
// shown on the workflow bar and in the job lists; there is no second bar.

import { useMemo } from 'react';
import { jobProgress, stepsForJob, type JobProgress } from '@zerogap/engine';
import { useRows, type Row } from './api';
import { useSession } from './session';

function work(job: Row, steps: Row[], dates: Row[], cards: Row[], doneState: string): JobProgress {
  const mine = stepsForJob(steps as any[], job);
  const dated = new Set(dates.filter((d) => d.job_id === job.id && d.actual_date).map((d) => d.workflow_step_id as string));
  return jobProgress(
    mine.map((s) => ({ key: s.key as string, done: dated.has(s.id) })),
    cards
      .filter((c) => c.job_id === job.id)
      .map((c) => {
        const closed = !!c.closed_at || c.state === doneState;
        return { hours_standard: c.hours_standard, hours_actual: c.hours_actual, pulled: closed || !!c.pulled_at, closed };
      }),
  );
}

/** Progress for every job at once: one read of the cards and of the step dates. */
export function useProgress(steps: Row[]): { loading: boolean; of: (job: Row) => JobProgress } {
  const s = useSession();
  const cards = useRows('work_card');
  const dates = useRows('job_step');
  const states = s.setting<string[]>('board_states', []);
  const done = states[states.length - 1] ?? 'Done';
  return useMemo(() => {
    const cache = new Map<string, JobProgress>();
    return {
      loading: cards.loading || dates.loading,
      of: (job: Row) => {
        if (!cache.has(job.id)) cache.set(job.id, work(job, steps, dates.rows, cards.rows, done));
        return cache.get(job.id)!;
      },
    };
  }, [cards.rows, cards.loading, dates.rows, dates.loading, steps, done]);
}
